// src/admin/components/reports/MonthlyPaymentsReport.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
    RiCalendarLine,
    RiMoneyDollarCircleLine,
    RiArrowLeftLine,
    RiArrowRightLine,
    RiDownloadLine,
    RiPrinterLine,
    RiRefreshLine,
    RiSearchLine,
    RiFilterLine,
    RiCheckLine,
    RiCloseLine,
    RiAlertLine,
    RiUserLine,
    RiBuildingLine,
    RiHomeLine,
    RiServiceLine,
    RiWallet3Line,
    RiBarChartBoxLine,
    RiArrowUpLine,
    RiArrowDownLine,
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { getPaymentLogs, formatCurrency, formatDate, getMonthStartEnd } from '../../../models/reportModel';
import { getAllServicePayments } from '../../../models/serviceModel';
import { getAllBuildings } from '../../../models/buildingModel';
import * as XLSX from 'xlsx';

const MONTHS = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
];

const STATUS_BADGE = {
    paid: {
        light: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
        dark: 'bg-emerald-900/30 text-emerald-400 border border-emerald-800',
    },
    unpaid: {
        light: 'bg-rose-100 text-rose-700 border border-rose-200',
        dark: 'bg-rose-900/30 text-rose-400 border border-rose-800',
    },
    partial: {
        light: 'bg-amber-100 text-amber-700 border border-amber-200',
        dark: 'bg-amber-900/30 text-amber-400 border border-amber-800',
    },
};

const MonthlyPaymentsReport = () => {
    const { t, i18n } = useTranslation();
    const { isDarkMode } = useTheme();
    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    const today = new Date();
    const [month, setMonth] = useState(today.getMonth() + 1);
    const [year, setYear] = useState(today.getFullYear());
    const [buildingId, setBuildingId] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [search, setSearch] = useState('');
    const [activeTab, setActiveTab] = useState('tenant'); // 'tenant' | 'service'
    const [tenantPayments, setTenantPayments] = useState([]);
    const [servicePayments, setServicePayments] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [loadingTenant, setLoadingTenant] = useState(true);
    const [loadingService, setLoadingService] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 15;

    // ─── Fetch Buildings ──────────────────────────────────────────────────────
    useEffect(() => {
        getAllBuildings()
            .then(data => {
                const list = Array.isArray(data) ? data
                    : data?.buildings ?? data?.data ?? [];
                setBuildings(list);
            })
            .catch(console.error);
    }, []);

    // ─── Fetch Tenant Payments ────────────────────────────────────────────────
    const fetchTenantPayments = useCallback(async () => {
        try {
            setLoadingTenant(true);
            const { startDate, endDate } = getMonthStartEnd(year, month);
            const params = { startDate, endDate, limit: 500, offset: 0 };
            if (buildingId) params.buildingId = buildingId;
            const res = await getPaymentLogs(params);
            setTenantPayments(res?.paymentLogs ?? []);
        } catch (err) {
            console.error('Error fetching tenant payments:', err);
            setError(err?.message || 'Failed to load tenant payments');
        } finally {
            setLoadingTenant(false);
        }
    }, [month, year, buildingId]);

    // ─── Fetch Service Payments ───────────────────────────────────────────────
    const fetchServicePayments = useCallback(async () => {
        try {
            setLoadingService(true);
            const params = { month, year, limit: 500, page: 1 };
            if (buildingId) params.buildingId = buildingId;
            const res = await getAllServicePayments(params);
            setServicePayments(res?.payments ?? []);
        } catch (err) {
            console.error('Error fetching service payments:', err);
            setError(err?.message || 'Failed to load service payments');
        } finally {
            setLoadingService(false);
        }
    }, [month, year, buildingId]);

    useEffect(() => {
        setPage(1);
        setError(null);
        fetchTenantPayments();
        fetchServicePayments();
    }, [fetchTenantPayments, fetchServicePayments]);

    // ─── Filtered Data ────────────────────────────────────────────────────────
    const filteredTenant = useMemo(() => {
        return tenantPayments.filter(p => {
            if (statusFilter === 'unpaid') return false; // tenant paymentLogs are only paid records
            if (search) {
                const q = search.toLowerCase();
                return (
                    (p.tenantName ?? '').toLowerCase().includes(q) ||
                    (p.apartmentNumber ?? '').toLowerCase().includes(q) ||
                    (p.buildingName ?? '').toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [tenantPayments, statusFilter, search]);

    const filteredService = useMemo(() => {
        return servicePayments.filter(p => {
            if (statusFilter === 'paid' && !p.isPaid) return false;
            if (statusFilter === 'unpaid' && p.isPaid) return false;
            if (search) {
                const q = search.toLowerCase();
                return (
                    (p.tenantName ?? p.ownerName ?? '').toLowerCase().includes(q) ||
                    (p.apartmentNumber ?? '').toLowerCase().includes(q) ||
                    (p.buildingName ?? '').toLowerCase().includes(q)
                );
            }
            return true;
        });
    }, [servicePayments, statusFilter, search]);

    // ─── Summary Stats ────────────────────────────────────────────────────────
    const tenantTotal = useMemo(() =>
        tenantPayments.reduce((s, p) => s + Number(p.amount || 0), 0),
        [tenantPayments]
    );
    const serviceTotal = useMemo(() =>
        servicePayments.filter(p => p.isPaid).reduce((s, p) => s + Number(p.amount || 0), 0),
        [servicePayments]
    );
    const servicePending = useMemo(() =>
        servicePayments.filter(p => !p.isPaid).reduce((s, p) => s + Number(p.amount || 0), 0),
        [servicePayments]
    );
    const totalRevenue = tenantTotal + serviceTotal;

    // ─── Pagination ───────────────────────────────────────────────────────────
    const activeList = activeTab === 'tenant' ? filteredTenant : filteredService;
    const totalPages = Math.max(1, Math.ceil(activeList.length / PAGE_SIZE));
    const pagedList = activeList.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setPage(1);
    };

    // ─── Export ───────────────────────────────────────────────────────────────
    const handleExport = () => {
        const monthName = MONTHS[month - 1];
        const rows = activeTab === 'tenant'
            ? filteredTenant.map(p => ({
                [t('name')]: p.tenantName ?? '',
                [t('apartment')]: p.apartmentNumber ?? '',
                [t('building')]: p.buildingName ?? '',
                [t('amount')]: p.amount,
                [t('status')]: t('paid'),
                [t('payment_date')]: formatDate(p.paymentDate),
            }))
            : filteredService.map(p => ({
                [t('name')]: p.tenantName ?? p.ownerName ?? '',
                [t('apartment')]: p.apartmentNumber ?? '',
                [t('floor')]: p.floor ?? '',
                [t('amount')]: p.amount,
                [t('status')]: p.isPaid ? t('paid') : t('unpaid'),
                [t('payment_date')]: p.paymentDate ? formatDate(p.paymentDate) : '',
            }));

        const ws = XLSX.utils.json_to_sheet(rows);
        ws['!cols'] = [{ wch: 25 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, { wch: 18 }];
        const wb = XLSX.utils.book_new();
        const sheetName = activeTab === 'tenant' ? t('tenant_payments') : t('service_payments');
        XLSX.utils.book_append_sheet(wb, ws, sheetName.slice(0, 31));
        XLSX.writeFile(wb, `monthly_payments_${monthName}_${year}.xlsx`);
    };

    // ─── Print ────────────────────────────────────────────────────────────────
    const handlePrint = () => {
        const monthName = MONTHS[month - 1];
        const rows = pagedList;
        const w = window.open('', '_blank');
        const isService = activeTab === 'service';
        w.document.write(`<!DOCTYPE html><html><head><title>Monthly Payments</title>
    <style>body{font-family:Arial;padding:20px;direction:${isRTL ? 'rtl' : 'ltr'}}
    h2{font-size:18px;text-align:center;margin-bottom:4px}
    .sub{text-align:center;color:#666;font-size:13px;margin-bottom:16px}
    table{width:100%;border-collapse:collapse}th,td{border:1px solid #ddd;padding:7px 10px;text-align:left;font-size:12px}
    th{background:#f5f5f5;font-weight:bold}.paid{color:#16a34a}.unpaid{color:#dc2626}
    .total{font-weight:bold;padding:10px 0;text-align:right}</style></head><body>
    <h2>${t('monthly_payments_report')}</h2>
    <div class="sub">${monthName} ${year} — ${isService ? t('service_payments') : t('tenant_payments')}</div>
    <table><thead><tr>
      <th>${t('name')}</th><th>${t('apartment')}</th><th>${t('floor')}</th>
      <th>${t('amount')}</th><th>${t('status')}</th><th>${t('payment_date')}</th>
    </tr></thead><tbody>
    ${rows.map(p => {
            const name = isService ? (p.tenantName ?? p.ownerName ?? '-') : (p.tenantName ?? '-');
            const apt = p.apartmentNumber ?? '-';
            const floor = p.floor ?? '-';
            const amt = formatCurrency(p.amount);
            const status = isService ? (p.isPaid ? `<span class="paid">${t('paid')}</span>` : `<span class="unpaid">${t('unpaid')}</span>`) : `<span class="paid">${t('paid')}</span>`;
            const date = p.paymentDate ? formatDate(p.paymentDate) : '-';
            return `<tr><td>${name}</td><td>${apt}</td><td>${floor}</td><td>${amt}</td><td>${status}</td><td>${date}</td></tr>`;
        }).join('')}
    </tbody></table>
    <div class="total">${t('total')}: ${formatCurrency(rows.reduce((s, p) => s + Number(p.amount || 0), 0))}</div>
    <script>window.onload=()=>{window.print();window.close()}<\/script></body></html>`);
        w.document.close();
    };

    // ─── Helpers ──────────────────────────────────────────────────────────────
    const cs = (...classes) => classes.filter(Boolean).join(' ');
    const card = cs(
        'p-5 rounded-2xl border shadow-sm transition-all duration-200',
        isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'
    );
    const th = cs(
        'px-4 py-3 text-xs font-semibold uppercase tracking-wider',
        isDarkMode ? 'text-zinc-400' : 'text-zinc-500'
    );
    const tbody = cs('divide-y', isDarkMode ? 'divide-zinc-800' : 'divide-zinc-100');
    const tdBase = cs('px-4 py-3 text-sm', isDarkMode ? 'text-zinc-300' : 'text-zinc-700');

    const years = Array.from({ length: 5 }, (_, i) => today.getFullYear() - 2 + i);

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">

            {/* ── Header ────────────────────────────────────────────────────────── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className={cs('text-2xl font-bold flex items-center gap-3', isDarkMode ? 'text-white' : 'text-zinc-900')}>
                        <span className={cs('p-2 rounded-xl', isDarkMode ? 'bg-red-900/30 text-red-400' : 'bg-red-50 text-red-600')}>
                            <RiCalendarLine size={22} />
                        </span>
                        {t('monthly_payments_report') || 'Monthly Payments Report'}
                    </h1>
                    <p className={cs('mt-1 text-sm', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                        {t('monthly_payments_subtitle') || 'Consolidated view of tenant rent and service payments'}
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => { fetchTenantPayments(); fetchServicePayments(); }}
                        className={cs('p-2 rounded-lg border transition-colors',
                            isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-600 hover:bg-zinc-50')}>
                        <RiRefreshLine size={18} />
                    </button>
                    <button onClick={handleExport}
                        className={cs('flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm',
                            isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50')}>
                        <RiDownloadLine size={16} />{t('export') || 'Export'}
                    </button>
                    <button onClick={handlePrint}
                        className={cs('flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm',
                            isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50')}>
                        <RiPrinterLine size={16} />{t('print') || 'Print'}
                    </button>
                    <Link to="/admin/reports"
                        className={cs('flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors text-sm',
                            isDarkMode ? 'bg-zinc-900 border-zinc-800 text-zinc-300 hover:bg-zinc-800' : 'bg-white border-zinc-200 text-zinc-700 hover:bg-zinc-50')}>
                        {isRTL ? <RiArrowRightLine size={16} /> : <RiArrowLeftLine size={16} />}
                        {t('back') || 'Back'}
                    </Link>
                </div>
            </div>

            {/* ── Filters ───────────────────────────────────────────────────────── */}
            <div className={cs('rounded-2xl border p-4', isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200')}>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    {/* Month */}
                    <div>
                        <label className={cs('text-xs font-medium mb-1.5 block', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                            {t('month') || 'Month'}
                        </label>
                        <select value={month} onChange={e => { setMonth(Number(e.target.value)); setPage(1); }}
                            className={cs('w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-red-500',
                                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')}>
                            {MONTHS.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                        </select>
                    </div>

                    {/* Year */}
                    <div>
                        <label className={cs('text-xs font-medium mb-1.5 block', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                            {t('year') || 'Year'}
                        </label>
                        <select value={year} onChange={e => { setYear(Number(e.target.value)); setPage(1); }}
                            className={cs('w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-red-500',
                                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')}>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                    </div>

                    {/* Building */}
                    <div>
                        <label className={cs('text-xs font-medium mb-1.5 block', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                            {t('building') || 'Building'}
                        </label>
                        <select value={buildingId} onChange={e => { setBuildingId(e.target.value); setPage(1); }}
                            className={cs('w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-red-500',
                                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')}>
                            <option value="">{t('all_buildings') || 'All Buildings'}</option>
                            {buildings.map(b => <option key={b.id || b._id} value={b.id || b._id}>{b.name}</option>)}
                        </select>
                    </div>

                    {/* Status */}
                    <div>
                        <label className={cs('text-xs font-medium mb-1.5 block', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                            {t('status') || 'Status'}
                        </label>
                        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
                            className={cs('w-full px-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-red-500',
                                isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white' : 'bg-zinc-50 border-zinc-200 text-zinc-900')}>
                            <option value="">{t('all_statuses') || 'All Statuses'}</option>
                            <option value="paid">{t('paid') || 'Paid'}</option>
                            <option value="unpaid">{t('unpaid') || 'Unpaid'}</option>
                        </select>
                    </div>

                    {/* Search */}
                    <div>
                        <label className={cs('text-xs font-medium mb-1.5 block', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                            {t('search') || 'Search'}
                        </label>
                        <div className="relative">
                            <RiSearchLine className="absolute start-3 top-1/2 -translate-y-1/2 text-zinc-400" size={15} />
                            <input type="text" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
                                placeholder={t('search') + '...'}
                                className={cs('w-full ps-9 pe-3 py-2 rounded-lg text-sm border outline-none focus:ring-2 focus:ring-red-500',
                                    isDarkMode ? 'bg-zinc-950 border-zinc-800 text-white placeholder:text-zinc-600' : 'bg-zinc-50 border-zinc-200 text-zinc-900 placeholder:text-zinc-400')}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Error ─────────────────────────────────────────────────────────── */}
            {error && (
                <div className={cs('flex items-center gap-2 p-4 rounded-xl border text-sm',
                    isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-rose-50 border-rose-200 text-rose-700')}>
                    <RiAlertLine size={18} /> {error}
                </div>
            )}

            {/* ── Summary Cards ─────────────────────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

                {/* Tenant Payments Card */}
                <div className={cs(card, 'hover:shadow-md')}>
                    <div className="flex items-start justify-between">
                        <div>
                            <p className={cs('text-xs font-semibold uppercase tracking-wider mb-1', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                                {t('tenant_payments') || 'Tenant Payments'}
                            </p>
                            {loadingTenant
                                ? <div className={cs('h-8 w-32 rounded-lg animate-pulse', isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100')} />
                                : <p className={cs('text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-zinc-900')}>
                                    {formatCurrency(tenantTotal)}
                                </p>
                            }
                            <p className={cs('text-xs mt-1.5', isDarkMode ? 'text-zinc-500' : 'text-zinc-400')}>
                                {loadingTenant ? '—' : `${tenantPayments.length} ${t('payments') || 'payments'}`}
                            </p>
                        </div>
                        <div className={cs('p-3 rounded-xl', isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-50 text-blue-600')}>
                            <RiUserLine size={22} />
                        </div>
                    </div>
                    <div className={cs('mt-4 pt-4 border-t flex items-center gap-1.5 text-xs',
                        isDarkMode ? 'border-zinc-800 text-emerald-400' : 'border-zinc-100 text-emerald-600')}>
                        <RiCheckLine size={14} />
                        <span>{t('all_paid') || 'All paid'}</span>
                    </div>
                </div>

                {/* Service Payments Card */}
                <div className={cs(card, 'hover:shadow-md')}>
                    <div className="flex items-start justify-between">
                        <div>
                            <p className={cs('text-xs font-semibold uppercase tracking-wider mb-1', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                                {t('service_payments') || 'Service Payments'}
                            </p>
                            {loadingService
                                ? <div className={cs('h-8 w-32 rounded-lg animate-pulse', isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100')} />
                                : <p className={cs('text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-zinc-900')}>
                                    {formatCurrency(serviceTotal)}
                                </p>
                            }
                            <p className={cs('text-xs mt-1.5', isDarkMode ? 'text-zinc-500' : 'text-zinc-400')}>
                                {loadingService ? '—' : `${servicePayments.filter(p => p.isPaid).length} ${t('paid') || 'paid'} · ${servicePayments.filter(p => !p.isPaid).length} ${t('unpaid') || 'unpaid'}`}
                            </p>
                        </div>
                        <div className={cs('p-3 rounded-xl', isDarkMode ? 'bg-violet-900/30 text-violet-400' : 'bg-violet-50 text-violet-600')}>
                            <RiServiceLine size={22} />
                        </div>
                    </div>
                    <div className={cs('mt-4 pt-4 border-t flex items-center gap-1.5 text-xs',
                        isDarkMode ? 'border-zinc-800 text-rose-400' : 'border-zinc-100 text-rose-600')}>
                        <RiAlertLine size={14} />
                        <span>{formatCurrency(servicePending)} {t('pending') || 'pending'}</span>
                    </div>
                </div>

                {/* Total Revenue Card */}
                <div className={cs(
                    'p-5 rounded-2xl border shadow-sm transition-all duration-200 hover:shadow-md',
                    isDarkMode
                        ? 'bg-gradient-to-br from-red-950/60 to-zinc-900 border-red-900/50'
                        : 'bg-gradient-to-br from-red-600 to-red-700 border-red-500'
                )}>
                    <div className="flex items-start justify-between">
                        <div>
                            <p className={cs('text-xs font-semibold uppercase tracking-wider mb-1', isDarkMode ? 'text-red-400' : 'text-red-100')}>
                                {t('total_revenue') || 'Total Revenue'}
                            </p>
                            {(loadingTenant || loadingService)
                                ? <div className="h-8 w-32 rounded-lg animate-pulse bg-white/10" />
                                : <p className={cs('text-3xl font-bold tracking-tight', isDarkMode ? 'text-white' : 'text-white')}>
                                    {formatCurrency(totalRevenue)}
                                </p>
                            }
                            <p className={cs('text-xs mt-1.5', isDarkMode ? 'text-zinc-400' : 'text-red-200')}>
                                {MONTHS[month - 1]} {year}
                            </p>
                        </div>
                        <div className={cs('p-3 rounded-xl', isDarkMode ? 'bg-red-900/40 text-red-400' : 'bg-white/20 text-white')}>
                            <RiWallet3Line size={22} />
                        </div>
                    </div>
                    <div className={cs('mt-4 pt-4 border-t flex items-center gap-1.5 text-xs',
                        isDarkMode ? 'border-red-900/40 text-zinc-400' : 'border-red-500/40 text-red-100')}>
                        <RiBarChartBoxLine size={14} />
                        <span>
                            {t('tenant_payments') || 'Tenant'}: {formatCurrency(tenantTotal)} ·{' '}
                            {t('service_payments') || 'Service'}: {formatCurrency(serviceTotal)}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Pill Switcher ─────────────────────────────────────────────────── */}
            <div className={cs('inline-flex p-1 rounded-full border gap-1',
                isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-zinc-100 border-zinc-200')}>
                {[
                    { key: 'tenant', label: t('tenant_payments') || 'Tenant Payments', icon: <RiUserLine size={15} /> },
                    { key: 'service', label: t('service_payments') || 'Service Payments', icon: <RiServiceLine size={15} /> },
                ].map(tab => (
                    <button
                        key={tab.key}
                        onClick={() => handleTabChange(tab.key)}
                        className={cs(
                            'flex items-center gap-2 px-5 py-2 rounded-full text-sm font-medium transition-all duration-200',
                            activeTab === tab.key
                                ? isDarkMode
                                    ? 'bg-red-600 text-white shadow-sm'
                                    : 'bg-white text-red-600 shadow-sm border border-zinc-200'
                                : isDarkMode
                                    ? 'text-zinc-400 hover:text-white'
                                    : 'text-zinc-500 hover:text-zinc-700'
                        )}
                    >
                        {tab.icon}
                        {tab.label}
                        <span className={cs(
                            'text-xs px-1.5 py-0.5 rounded-full font-semibold',
                            activeTab === tab.key
                                ? isDarkMode ? 'bg-white/20' : 'bg-red-50 text-red-500'
                                : isDarkMode ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-200 text-zinc-600'
                        )}>
                            {tab.key === 'tenant' ? filteredTenant.length : filteredService.length}
                        </span>
                    </button>
                ))}
            </div>

            {/* ── Table ─────────────────────────────────────────────────────────── */}
            <div className={cs('rounded-2xl border overflow-hidden shadow-sm',
                isDarkMode ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white')}>

                {/* Table header row */}
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                        <thead>
                            <tr className={cs(isDarkMode ? 'bg-zinc-950' : 'bg-zinc-50')}>
                                <th className={cs(th, 'text-start')}>{t('name') || 'Name'}</th>
                                <th className={cs(th, 'text-start')}>{t('apartment') || 'Apartment'}</th>
                                <th className={cs(th, 'text-start')}>{t('floor') || 'Floor'}</th>
                                <th className={cs(th, 'text-start')}>{t('amount') || 'Amount'}</th>
                                <th className={cs(th, 'text-start')}>{t('status') || 'Status'}</th>
                                <th className={cs(th, 'text-start')}>{t('payment_date') || 'Payment Date'}</th>
                            </tr>
                        </thead>
                        <tbody className={tbody}>
                            {/* Loading skeleton */}
                            {(activeTab === 'tenant' ? loadingTenant : loadingService)
                                ? Array.from({ length: 6 }).map((_, i) => (
                                    <tr key={i} className="animate-pulse">
                                        {Array.from({ length: 6 }).map((_, j) => (
                                            <td key={j} className="px-4 py-3">
                                                <div className={cs('h-4 rounded', isDarkMode ? 'bg-zinc-800' : 'bg-zinc-100',
                                                    j === 0 ? 'w-32' : j === 3 ? 'w-20' : j === 4 ? 'w-16' : 'w-24')} />
                                            </td>
                                        ))}
                                    </tr>
                                ))
                                : pagedList.length === 0
                                    ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-14 text-center">
                                                <div className="flex flex-col items-center gap-3">
                                                    <div className={cs('p-4 rounded-full', isDarkMode ? 'bg-zinc-800 text-zinc-600' : 'bg-zinc-50 text-zinc-300')}>
                                                        <RiCalendarLine size={36} />
                                                    </div>
                                                    <p className={cs('text-sm font-medium', isDarkMode ? 'text-zinc-400' : 'text-zinc-500')}>
                                                        {t('no_payments_found') || 'No payments found'}
                                                    </p>
                                                    <p className={cs('text-xs', isDarkMode ? 'text-zinc-600' : 'text-zinc-400')}>
                                                        {t('try_adjusting_filters') || 'Try adjusting the month, year, or filters'}
                                                    </p>
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                    : pagedList.map((p, i) => {
                                        const isService = activeTab === 'service';
                                        const name = isService ? (p.tenantName ?? p.ownerName ?? '-') : (p.tenantName ?? '-');
                                        const apt = p.apartmentNumber ?? '-';
                                        const floor = p.floor != null ? p.floor : '-';
                                        const amt = Number(p.amount ?? 0);
                                        const isPaid = isService ? !!p.isPaid : true; // tenant paymentLogs are all paid
                                        const date = p.paymentDate ? formatDate(p.paymentDate) : '-';

                                        const badge = STATUS_BADGE[isPaid ? 'paid' : 'unpaid'];

                                        return (
                                            <tr key={p.paymentId ?? p.id ?? i}
                                                className={cs('transition-colors',
                                                    isDarkMode ? 'hover:bg-zinc-800/60' : 'hover:bg-zinc-50/80')}>
                                                {/* Name */}
                                                <td className={tdBase}>
                                                    <div className="flex items-center gap-2">
                                                        <div className={cs('w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0',
                                                            isDarkMode ? 'bg-zinc-800 text-zinc-300' : 'bg-zinc-100 text-zinc-600')}>
                                                            {name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <span className="font-medium">{name}</span>
                                                    </div>
                                                </td>
                                                {/* Apartment */}
                                                <td className={tdBase}>
                                                    <div className="flex items-center gap-1.5">
                                                        <RiHomeLine size={14} className={isDarkMode ? 'text-zinc-500' : 'text-zinc-400'} />
                                                        {apt}
                                                    </div>
                                                </td>
                                                {/* Floor */}
                                                <td className={tdBase}>{floor}</td>
                                                {/* Amount */}
                                                <td className={tdBase}>
                                                    <span className={cs('font-semibold', isDarkMode ? 'text-white' : 'text-zinc-900')}>
                                                        {formatCurrency(amt)}
                                                    </span>
                                                </td>
                                                {/* Status */}
                                                <td className={tdBase}>
                                                    <span className={cs('inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold',
                                                        isDarkMode ? badge.dark : badge.light)}>
                                                        {isPaid ? <RiCheckLine size={11} /> : <RiCloseLine size={11} />}
                                                        {isPaid ? (t('paid') || 'Paid') : (t('unpaid') || 'Unpaid')}
                                                    </span>
                                                </td>
                                                {/* Payment Date */}
                                                <td className={tdBase}>
                                                    <span className={isDarkMode ? 'text-zinc-400' : 'text-zinc-500'}>{date}</span>
                                                </td>
                                            </tr>
                                        );
                                    })
                            }
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loadingTenant && !loadingService && activeList.length > PAGE_SIZE && (
                    <div className={cs('px-4 py-3 flex items-center justify-between border-t',
                        isDarkMode ? 'border-zinc-800' : 'border-zinc-100')}>
                        <p className={cs('text-xs', isDarkMode ? 'text-zinc-500' : 'text-zinc-400')}>
                            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, activeList.length)} of {activeList.length}
                        </p>
                        <div className="flex items-center gap-1.5">
                            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                className={cs('p-1.5 rounded-lg transition-colors disabled:opacity-30',
                                    isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200')}>
                                {isRTL ? <RiArrowRightLine size={16} /> : <RiArrowLeftLine size={16} />}
                            </button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                                .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                                .map((p, i, arr) => (
                                    <React.Fragment key={p}>
                                        {i > 0 && arr[i - 1] !== p - 1 && (
                                            <span className={cs('px-1 text-xs', isDarkMode ? 'text-zinc-600' : 'text-zinc-400')}>…</span>
                                        )}
                                        <button onClick={() => setPage(p)}
                                            className={cs('w-7 h-7 rounded-lg text-xs transition-colors',
                                                page === p
                                                    ? isDarkMode ? 'bg-red-600 text-white' : 'bg-red-600 text-white'
                                                    : isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200')}>
                                            {p}
                                        </button>
                                    </React.Fragment>
                                ))}
                            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                                className={cs('p-1.5 rounded-lg transition-colors disabled:opacity-30',
                                    isDarkMode ? 'bg-zinc-800 text-zinc-300 hover:bg-zinc-700' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200')}>
                                {isRTL ? <RiArrowLeftLine size={16} /> : <RiArrowRightLine size={16} />}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonthlyPaymentsReport;
